import React, {useState} from "react";
import axios from "axios";
import {Box, Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Table, Tbody, Td, Text, Th, Thead, Tr,
  IconButton, useToast} from "@chakra-ui/react";
import {MdDeleteOutline} from "react-icons/md";

const API = "https://api.pureworker.com/api/admin";
const auth = () => ({timeout: 30000, headers: {Authorization: `Bearer ${localStorage.getItem("AuthToken")}`}});
type Account = {id: string; email: string; name: string; accountType: string};
type Result = {id: string; status: "deleted" | "already_deleted" | "skipped" | "failed"};
const labels = {deleted: "Removed", already_deleted: "Already removed", skipped: "Changed — skipped", failed: "Needs attention"};

export function DeleteAccountButton({account, onRemoved}: {account: any; onRemoved: () => void}) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function remove() {
    const id = account._id || account.id;
    if (!id || !window.confirm(`Delete ${account.email || "this account"}? This ends their access and keeps an archived record.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/users/${encodeURIComponent(id)}`, auth());
      toast({title: "Account deleted", status: "success"});
      onRemoved();
    } catch {
      toast({title: "Could not finish deletion", description: "Refresh to check the account, then retry if needed.", status: "error"});
    } finally { setBusy(false); }
  }
  return <IconButton aria-label={`Delete ${account.email || "account"}`} icon={<MdDeleteOutline />}
    colorScheme="red" isLoading={busy} isDisabled={Boolean(account.deleted)}
    onClick={event => {event.stopPropagation(); void remove();}} />;
}

export default function MailinatorCleanup({onRemoved}: {onRemoved: () => void}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [users, setUsers] = useState<Account[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const busy = loading || removing;
  const status = new Map(results.map(r => [r.id, labels[r.status]]));
  const remaining = users.filter(u => !results.some(r => r.id === u.id && r.status !== "failed"));
  const removed = results.filter(r => r.status === "deleted" || r.status === "already_deleted").length;

  async function preview() {
    setOpen(true); setLoading(true); setLoaded(false); setUsers([]); setResults([]); setConfirmation(""); setError("");
    try {
      const all: Account[] = [];
      let after: string | null = null;
      do {
        const response: {data: {users: Account[]; nextCursor: string | null}} = await axios.get(
          `${API}/users/mailinator-cleanup${after ? `?after=${encodeURIComponent(after)}` : ""}`, auth());
        all.push(...response.data.users);
        after = response.data.nextCursor;
      } while (after);
      setUsers(all); setLoaded(true);
    } catch { setError("Could not load matching accounts. Nothing has been removed. Try again."); }
    finally { setLoading(false); }
  }

  async function remove() {
    if ((!loaded && !results.length) || busy || confirmation !== "mailinator.com" || !remaining.length) return;
    setRemoving(true); setError("");
    const completed: Result[] = results.filter(r => r.status !== "failed");
    try {
      for (let start = 0; start < remaining.length; start += 25) {
        const response = await axios.post(`${API}/users/mailinator-cleanup`, {
          confirmation, users: remaining.slice(start, start + 25).map(({id, email}) => ({id, email})),
        }, auth());
        completed.push(...response.data.results);
        setResults([...completed]);
      }
      if (completed.some(r => r.status === "failed"))
        setError("Some accounts need attention. You can retry the marked accounts to finish their cleanup.");
    } catch {
      setError("Cleanup was interrupted. Some accounts may have been removed. Retry the remaining accounts to finish, or refresh the preview.");
    } finally { setRemoving(false); onRemoved(); }
  }

  return <>
    <Button size="sm" colorScheme="red" variant="outline" mb={4} onClick={() => void preview()}>Remove Mailinator accounts</Button>
    <Modal isOpen={open} onClose={() => {if (!busy) setOpen(false);}} size="xl" closeOnOverlayClick={!busy} closeOnEsc={!busy}>
      <ModalOverlay /><ModalContent><ModalHeader>Remove Mailinator accounts</ModalHeader>
        {!busy && <ModalCloseButton />}
        <ModalBody>
          <Text mb={3}>This covers customers and service providers whose email ends exactly in @mailinator.com. Their access will end; archived records will remain available under “Show deleted accounts”.</Text>
          {loading ? <Text>Loading matching accounts…</Text> : <Text fontWeight="bold" mb={3}>{users.length} matching account(s)</Text>}
          {error && <Text role="alert" color="red.500" mb={3}>{error}</Text>}
          {!!results.length && <Text role="status" mb={3}>{removed} removed or already removed · {results.length} of {users.length} checked</Text>}
          {!!users.length && <Box maxH="300px" overflow="auto"><Table size="sm">
            <Thead><Tr><Th>Email</Th><Th>Account</Th><Th>Status</Th></Tr></Thead>
            <Tbody>{users.map(u => <Tr key={u.id}><Td>{u.email}<Text fontSize="xs">{u.name}</Text></Td>
              <Td>{u.accountType}</Td><Td>{status.get(u.id) || "Awaiting confirmation"}</Td></Tr>)}</Tbody>
          </Table></Box>}
          {loaded && !!users.length && !results.length && <Box mt={4}>
            <Text as="label" htmlFor="mailinator-confirm">Type mailinator.com to confirm removal of these {users.length} accounts.</Text>
            <Input id="mailinator-confirm" mt={2} value={confirmation} onChange={e => setConfirmation(e.target.value)} isDisabled={busy} autoComplete="off" />
          </Box>}
        </ModalBody>
        <ModalFooter gap={3} flexWrap="wrap">
          <Button onClick={() => setOpen(false)} isDisabled={busy}>Close</Button>
          <Button onClick={() => void preview()} isDisabled={busy}>Refresh preview</Button>
          <Button colorScheme="red" onClick={() => void remove()} isLoading={removing} loadingText="Removing accounts"
            isDisabled={busy || (!loaded && !results.length) || !remaining.length || confirmation !== "mailinator.com"}>{results.length ? "Retry remaining accounts" : `Remove ${users.length} accounts`}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  </>;
}
